import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import {
	useContributionsPageData,
	useContributionsPageState,
} from '#src/app/pages/accountSettings/contributions/main/pageContext';

type Result = ModelHookResult<
	{
		title: string;
		subtitle: string;
		updateContributionSubtitle: string;
		buttonLabel: string;
		isSupportedCountry: boolean;
	},
	EmptyObject
>;

// eslint-disable-next-line max-lines-per-function
export function useModifyContributions(): Result {
	const [
		{
			subscription: { amount, currencyCode, nextChargeAtLocalized, interval },
		},
	] = useContributionsPageState();
	const {
		data: {
			featureConfigs: { shouldUseDonationLanguage, membershipPPMUEnabled, membershipEnabled },
		},
	} = useContributionsPageData();

	const { localizeCurrency, translate, amountToBaseUnits } = useI18n();

	const amountInBaseUnits = amountToBaseUnits(amount, currencyCode);
	const dailyAmount = interval === 'YEAR' ? amountInBaseUnits / 365 : amountInBaseUnits / 30;
	const dailyAmountLocalized = localizeCurrency(dailyAmount, currencyCode, {
		currencyDisplay: 'symbol',
		amountInBaseUnits: true,
		forceFractionDigits: true,
	});

	const dailyApproximation = translate('fe.pages.subscriptions.manage.daily_contribution_estimate', {
		dailyAmount: dailyAmountLocalized,
	});

	const amountLocalized = localizeCurrency(amountInBaseUnits, currencyCode, {
		currencyDisplay: 'symbol',
		amountInBaseUnits: true,
		forceFractionDigits: true,
	});

	const i18nSuffix = shouldUseDonationLanguage ? '_donation' : '';

	const subtitle = useMemo(() => {
		return interval === 'YEAR'
			? translate(`corgi.membership.manage.current_yearly_contribution${i18nSuffix}`, {
					amount: amountLocalized,
					dailyApproximation,
					nextChargeDate: nextChargeAtLocalized,
				})
			: translate(`fe.pages.subscriptions.manage.current_contribution${i18nSuffix}`, {
					amount: amountLocalized,
					dailyApproximation,
					nextChargeDate: nextChargeAtLocalized,
				});
	}, [amountLocalized, dailyApproximation, i18nSuffix, interval, nextChargeAtLocalized, translate]);

	const updateContributionSubtitle = useMemo(() => {
		return translate(
			`corgi.membership.manage.update_${shouldUseDonationLanguage ? 'donation' : 'contribution'}_description`,
		);
	}, [shouldUseDonationLanguage, translate]);

	return {
		data: {
			title: translate(`corgi.membership.manage.modify_contributions${i18nSuffix}`),
			subtitle,
			updateContributionSubtitle,
			buttonLabel: translate(`fe.pages.subscriptions.manage.change_contribution_amount${i18nSuffix}`),
			isSupportedCountry: membershipEnabled || membershipPPMUEnabled,
		},
		actions: {},
	};
}
